import { Capacitor } from "@capacitor/core";
import { PushNotifications, PushNotificationSchema } from "@capacitor/push-notifications";
import { useAtomValue } from "jotai";
import React from "react";
import { ReceivedFcm } from "../activities/AppNavigator";
import { API } from "../API";
import { disablePushNotificationsChecksAtom } from "../hooks/useDisablePushNotifications";
import { FcmPushNotification, NotificationType } from "../messages";

interface Props {
  api: API;
  onClickPushNotification: (data: FcmPushNotification) => void;
  onRequestDisplayNotificationBanner: (message: string, data: FcmPushNotification | null) => void;
  onUpdatePushNotificationStatus: (enabled: boolean) => void;
}

function PushNotificationsListener(props: Props) {
  const disableChecks = useAtomValue(disablePushNotificationsChecksAtom);

  const shouldDisplayNotificationBanner = React.useCallback(
    (fcm: ReceivedFcm) => {
      if (fcm.message === null) {
        return false;
      }

      for (const check of disableChecks) {
        if (check.disableIf(fcm.data?.data)) {
          return false;
        }
      }

      return true;
    },
    [disableChecks]
  );

  const handleFcmMessageReceived = React.useCallback(
    (notification: PushNotificationSchema) => {
      const fcm = parseRawFcmData(notification.data);

      if (fcm.tap === null) {
        if (fcm.message !== null && shouldDisplayNotificationBanner(fcm)) {
          props.onRequestDisplayNotificationBanner.call(null, fcm.message, fcm.data);
        }
        return;
      }

      if (fcm.data !== null) {
        props.onClickPushNotification.call(null, fcm.data);
      }
    },
    [
      props.onClickPushNotification,
      props.onRequestDisplayNotificationBanner,
      shouldDisplayNotificationBanner,
    ]
  );

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return void console.log("[fcm] skipping. not native platform");
    }

    registerPushNotifications().then((result) => {
      props.onUpdatePushNotificationStatus(result);
      if (!result) {
        return;
      }

      PushNotifications.addListener("registration", (token) => {
        console.log("[fcm] registration", token);

        props.api.registerFcmToken({ token: token.value });
      });

      PushNotifications.addListener("registrationError", (error) => {
        console.log("[fcm] registration error", error);
      });

      PushNotifications.addListener("pushNotificationReceived", (notification) => {
        console.log("[fcm] push notification received", notification);
        handleFcmMessageReceived(notification);
      });

      PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
        console.log("[fcm] push notification action performed", notification);
        handleFcmMessageReceived({
          ...notification.notification,
          data: {
            ...notification.notification.data,
            tap: "background",
          },
        });
      });
    });

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [disableChecks, handleFcmMessageReceived, props]);

  return null;
}

// TODO - I'm not sure what's fcm.message. fcm should be: PushNotificationSchema
function parseRawFcmData(fcm: any): ReceivedFcm {
  let message: string | null;
  if (typeof fcm.body === "string") {
    message = fcm.body;
  } else if (typeof fcm.message === "string") {
    message = fcm.message;
  } else {
    message = null;
  }

  let tap: "foreground" | "background" | null;

  if (fcm.tap === "foreground" || fcm.tap === "background") {
    tap = fcm.tap;
  } else {
    tap = null;
  }

  let data: FcmPushNotification | null = null;
  if (typeof fcm.data === "string") {
    let json: any = null;
    try {
      json = JSON.parse(fcm.data);
    } catch {
      // `json` remains null
    }
    if (json !== null) {
      if (typeof json.data === "object") {
        if (typeof json.data.type === "string") {
          if (isValidNotificationType(json.data.type)) {
            data = json;
          }
        }
      }
    }
  }

  return {
    tap: tap,
    message: message,
    data: data,
  };
}

function isValidNotificationType(type: string): boolean {
  return Object.keys(NotificationType).indexOf(type) >= 0;
}

async function registerPushNotifications() {
  console.log("[fcm] registering");

  let permissionStatus = await PushNotifications.checkPermissions();

  console.log("[fcm] permission status pre request", permissionStatus);

  if (permissionStatus.receive === "prompt") {
    permissionStatus = await PushNotifications.requestPermissions();
  }

  console.log("[fcm] permission status post request", permissionStatus);

  if (permissionStatus.receive !== "granted") {
    return false;
  }

  await PushNotifications.register();

  return true;
}

export default React.memo(PushNotificationsListener, () => true);
